/**
 * Hook pour gérer le profil utilisateur
 * @module pages/hooks/useProfile
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../utils/storage';
import { STORAGE_KEYS } from '../../config/constants';
import { useUserSession } from '../../hooks/useUserSession';
import { resolveProfileTwitchUser } from './resolveProfileTwitchUser';

/**
 * Hook de gestion du profil
 * @returns {Object} État et données du profil
 */
export const useProfile = () => {
  const navigate = useNavigate();
  const {
    user: sessionUser,
    isLoading: sessionLoading,
    getTwitchUser,
  } = useUserSession();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionLoading) {
      setLoading(true);
      return;
    }

    try {
      const twitchUser = resolveProfileTwitchUser({
        getTwitchUser,
        sessionUser,
        storageGet: (key) => storage.get(key),
        storageKey: STORAGE_KEYS.TWITCH_USER,
      });
      setUser(twitchUser);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement du profil:', err);
      setError('Erreur lors du chargement des informations du profil');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [sessionLoading, sessionUser, getTwitchUser]);

  /**
   * Déconnecte l'utilisateur
   */
  const handleLogout = () => {
    try {
      storage.clearAuth();
      navigate('/');
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
      setError('Erreur lors de la déconnexion');
    }
  };

  return {
    user,
    loading,
    error,
    handleLogout,
  };
};
